import { useEffect, useRef, useState } from "react";
import { X } from "@phosphor-icons/react";
import { getEditorConfig } from "@/lib/officeTools";

const loadedScripts = new Map();

function loadDocsApi(origin) {
  const src = `${String(origin).replace(/\/$/, "")}/web-apps/apps/api/documents/api.js`;
  if (window.DocsAPI?.DocEditor) return Promise.resolve();
  if (loadedScripts.has(src)) return loadedScripts.get(src);
  const promise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => window.DocsAPI?.DocEditor
      ? resolve()
      : reject(new Error("ONLYOFFICE API did not initialize"));
    script.onerror = () => reject(new Error("ONLYOFFICE API could not be loaded"));
    document.head.appendChild(script);
  });
  loadedScripts.set(src, promise);
  return promise;
}

export default function OnlyOfficeEditor({ workspaceId, file, onClose }) {
  const editorRef = useRef(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let active = true;
    const hostId = `agenaix-office-editor-${file.file_id}`;
    (async () => {
      try {
        const payload = await getEditorConfig(workspaceId, file.file_id);
        await loadDocsApi(payload.document_server_url);
        if (!active) return;
        const config = {
          ...payload.config,
          width: "100%",
          height: "100%",
          events: {
            onDocumentReady: () => active && setState("ready"),
            onError: () => active && setState("error"),
            onRequestClose: onClose,
          },
        };
        editorRef.current = new window.DocsAPI.DocEditor(hostId, config);
      } catch {
        if (active) setState("error");
      }
    })();
    return () => {
      active = false;
      try { editorRef.current?.destroyEditor?.(); } catch { /* editor already gone */ }
      editorRef.current = null;
    };
  }, [file.file_id, workspaceId, onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col" role="dialog" aria-modal="true" aria-label={`Edit ${file.title}`}>
      <div className="h-12 px-3 border-b border-black/10 flex items-center gap-3 bg-white">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm truncate" style={{ unicodeBidi: "plaintext" }}>{file.title}</div>
          <div className="text-xs text-neutral-500 uppercase">{file.file_type} · v{file.version}</div>
        </div>
        <button type="button" onClick={onClose} className="h-9 w-9 rounded-md border border-black/10 flex items-center justify-center" aria-label="Close editor">
          <X size={18} />
        </button>
      </div>
      <div className="relative flex-1 min-h-0 bg-[#F4F6F8]">
        {state === "loading" && <div className="absolute inset-0 grid place-items-center text-sm text-neutral-600">Loading secure editor…</div>}
        {state === "error" && (
          <div className="absolute inset-0 grid place-items-center p-6 text-center">
            <div>
              <div className="font-semibold">Editor unavailable</div>
              <p className="text-sm text-neutral-600 mt-1">Check the ONLYOFFICE server configuration and try again.</p>
            </div>
          </div>
        )}
        <div id={`agenaix-office-editor-${file.file_id}`} className="w-full h-full" />
      </div>
    </div>
  );
}
